import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Pencil, Trash2, Users, Copy } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Creator {
  creator_name: string;
  joyabuy: string;
  kakobuy: string;
  cnfans: string;
  hoobuy: string;
  acbuy: string;
  oopbuy: string;
  mulebuy: string;
  orientdig: string;
  superbuy: string;
  ootdbuy: string;
  cssbuy: string;
  created_at: string;
}

const CreatorsList = () => {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('creators')
        .select('*')
        .order('creator_name', { ascending: true });

      if (error) {
        throw error;
      }

      setCreators(data || []);
    } catch (error) {
      console.error('Error fetching creators:', error);
      toast.error('Failed to load creators data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCreator = async (creatorName: string) => {
    try {
      setDeleting(true);
      const { error } = await supabase
        .from('creators')
        .delete()
        .eq('creator_name', creatorName);

      if (error) {
        throw error;
      }

      setCreators(prevCreators => prevCreators.filter(creator => creator.creator_name !== creatorName));
      toast.success('Creator deleted successfully');
    } catch (error) {
      console.error('Error deleting creator:', error);
      toast.error('Failed to delete creator. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyCreatorLink = async (creatorName: string) => {
    try {
      const creatorLink = `https://replibrary.com?creator=${encodeURIComponent(creatorName)}`;
      await navigator.clipboard.writeText(creatorLink);
      toast.success('Creator link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link to clipboard');
    }
  };

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div>
          <h1 className="text-3xl font-bold mb-1">Creator Management</h1>
          <p className="text-muted-foreground">Manage creators and their buying accounts</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <Link to="/admin/creators/new">
            <Button className="flex items-center gap-2">
              <Plus size={16} />
              Add Creator
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center h-60 bg-card rounded-lg border">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-muted-foreground">Loading creators data...</p>
          </div>
        </div>
      ) : (
        <Card>
          <CardHeader className="border-b bg-card/50">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Creators
                </CardTitle>
                <CardDescription>All creators and their buying accounts</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {creators.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="rounded-full bg-muted p-3 mb-4">
                  <Users className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-1">No creators found</h3>
                <p className="text-sm text-muted-foreground text-center max-w-sm mb-4">
                  Get started by adding your first creator.
                </p>
                <Link to="/admin/creators/new">
                  <Button size="sm" className="flex items-center gap-2">
                    <Plus size={14} />
                    Add Creator
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Creator Name</TableHead>
                      <TableHead>Joyabuy</TableHead>
                      <TableHead>Kakobuy</TableHead>
                      <TableHead>CNFans</TableHead>
                      <TableHead>Hoobuy</TableHead>
                      <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {creators.map((creator) => (
                      <TableRow key={creator.creator_name}>
                        <TableCell className="font-medium">{creator.creator_name}</TableCell>
                        <TableCell>{creator.joyabuy || '-'}</TableCell>
                        <TableCell>{creator.kakobuy || '-'}</TableCell>
                        <TableCell>{creator.cnfans || '-'}</TableCell>
                        <TableCell>{creator.hoobuy || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleCopyCreatorLink(creator.creator_name)}
                              title="Copy creator link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Link to={`/admin/creators/${encodeURIComponent(creator.creator_name)}`}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </Link>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-background border border-border/50">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the creator "{creator.creator_name}". This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-background hover:bg-muted">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-white hover:bg-destructive/90"
                                    onClick={() => handleDeleteCreator(creator.creator_name)}
                                    disabled={deleting}
                                  >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CreatorsList; 